<script>
import {
  iBadge,
  iDropzone,
  iDropzoneTypes,
  iFilter,
  iIcon,
  iIconButton,
  iModal,
  iTextInput,
  iToast,
  openModal,
  closeModal,
} from '@inscribe/signal';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import axios from 'axios';

export default {
  components: {
    iBadge,
    iDropzone,
    iFilter,
    iIcon,
    iIconButton,
    iModal,
    iTextInput,
    iToast,
  },
  data() {
    return {
      iDropzoneTypes,
      customerName: '',
      errors: [],
      isLoading: false,
      files: [],
      selectedTags: [],
    };
  },
  computed: {
    ...mapGetters({
      getTagsList: 'getTagsList',
    }),
    tags() {
      return this.getTagsList.map((tagObject) => {
        return {
          label: tagObject.value,
        };
      });
    },
    customerNameValid() {
      return this.customerName?.length > 2;
    },
  },
  mounted() {
    this.$store.commit('setSelectedCustomerId', undefined);
    openModal('new-customer-modal');
  },
  methods: {
    ...mapActions(['createNewCustomer']),
    ...mapMutations(['addTags']),
    openModal,
    closeModal,
    onFilesSelected(files) {
      this.files = [...this.files, ...files];
    },
    onTagsSelected(tags) {
      this.selectedTags = tags.selection.map((tag) => tag.label);
    },
    onTagCreateButtonClicked(tag) {
      this.addTags([
        {
          value: tag,
        },
      ]);
      this.selectedTags.push(tag);
    },
    deleteTag(tag) {
      if (!this.disabled) {
        const tagIndex = this.selectedTags.findIndex(
          (selectedTag) => selectedTag === tag,
        );
        if (tagIndex > -1) {
          this.selectedTags.splice(tagIndex, 1);
        }
      }
    },
    deleteFile(file) {
      const foundIndex = this.files.findIndex((f) => f === file);
      if (foundIndex > -1) {
        this.files.splice(foundIndex, 1);
      }
    },
    loadCustomerRoute() {
      this.$router.push({ name: 'customers-view', query: this.$route.query });
    },
    onClose() {
      if (!this.isLoading) {
        this.loadCustomerRoute();
      }
    },
    onCustomerNameChange(value) {
      this.customerName = value;
    },
    createCustomer() {
      this.isLoading = true;
      this.createNewCustomer({ name: this.customerName }).then(
        async (response) => {
          const customerData = response.data;
          this.$store.commit('addCustomer', response.data);
          this.showMessage({
            title: 'Success',
            text: 'Customer successfully created. You can now add documents to this customer and start checking for fraud.',
          });
          if (this.files.length) {
            await this.uploadFiles(customerData);
          }
          closeModal('new-customer-modal');
          this.$router.push({
            name: 'customer-details',
            params: { customer_id: response.data.id },
          });
          this.isLoading = false;
        },
        (error) => {
          this.showMessage({
            isError: true,
            title: 'Error',
            text: error?.response?.data?.message || '',
          });
          this.isLoading = false;
        },
      );
    },
    uploadFiles(customerData) {
      return new Promise((resolve, reject) => {
        const newCustomerUrl = `api/v2/customers/${customerData.id}/documents`;
        const formData = new FormData();
        for (const file of this.files) {
          formData.append('file', file);
        }
        // tags
        formData.append(
          'tags',
          JSON.stringify(
            this.selectedTags.map((tagLabel) => {
              return {
                text: tagLabel,
              };
            }),
          ),
        );

        const self = this;
        axios.post(newCustomerUrl, formData).then(
          function (response) {
            self.showMessage({
              title: 'Success',
              text: 'Your document(s) were successfully uploaded. We are processing them now, they will be available in a few moments.',
            });
            resolve(response);
          },
          function (error) {
            reject(error);
          },
        );
      });
    },
    showMessage(options = {}) {
      this.$notify({
        group: 'api',
        type: options.isError ? 'error' : 'success',
        title: options.title,
        duration: options.duration || 5000,
        text: options.text || '',
      });
    },
  },
};
</script>

<template>
  <div class="new-customer">
    <i-modal
      ref="myModal"
      modal-id="new-customer-modal"
      title="New customer"
      :close-on-click-outside="false"
      :close-on-submit="false"
      :confirm-button-loading="isLoading"
      :confirm-button-disabled="isLoading || !customerNameValid"
      :cancel-button-disabled="isLoading"
      :close-button-disabled="isLoading"
      :submit-on-enter="true"
      confirm-button-text="Create"
      @on-submit="createCustomer"
      @on-close="onClose"
    >
      <div
        slot="body-content"
        class="d-flex flex-column i-gap-10"
        data-testid="new-customer-modal"
      >
        <i-text-input
          label="Customer Name"
          :value="customerName"
          placeholder="Customer #1234"
          @input="onCustomerNameChange"
        ></i-text-input>

        <!-- files list -->
        <div
          v-if="files.length > 0"
          class="files-list d-flex flex-column i-gap-2"
        >
          <div
            v-for="(file, index) in Array.from(files)"
            :key="index"
            class="file-item d-flex flex-row align-items-center i-radius-sm i-px-6 i-py-5 i-text-subheading-2 i-text-neutral-120"
          >
            <i-icon
              symbol="small-file"
              :size="16"
              class="file-icon i-mr-6"
            ></i-icon>
            <div class="name flex-grow-1">{{ file.name }}</div>
            <i-icon-button
              symbol="medium-delete-1"
              size="xs"
              type="transparent"
              :disabled="isLoading"
              @button-click="deleteFile(file)"
            ></i-icon-button>
          </div>
        </div>
        <i-dropzone
          title="Choose file or drag and drop"
          sub-title="PDFs, JPEGs and PNGs accepted"
          :accepted-extensions="['pdf', 'jpg', 'jpeg', 'png']"
          :disabled="isLoading"
          @files-selected="onFilesSelected"
        ></i-dropzone>
        <div class="d-flex flex-column i-gap-6">
          <i-filter
            data-testid="assign-tags-filter"
            label="Tag these documents"
            title="Select the tags"
            search-input-placeholder="Search or Create"
            :options="tags"
            :searchable="true"
            :max-selected-extended-list="2"
            :value="selectedTags"
            :show-selected-list="false"
            :show-empty-create-button="true"
            :enabled="!isLoading"
            empty-create-button-label="Create `${value}` tag"
            @selection-changed="onTagsSelected"
            @create-button-clicked="onTagCreateButtonClicked"
          />
          <div
            v-if="selectedTags.length"
            class="tags-list d-flex flex-row i-gap-3"
          >
            <i-badge
              v-for="(tag, index) in selectedTags"
              :key="index"
              :label="tag"
              :has-trailing-icon="true"
              :interactable="true"
              :outline="true"
              icon="small-delete-1"
              @right-icon-click="deleteTag(tag)"
            >
            </i-badge>
          </div>
        </div>
      </div>
    </i-modal>
    <i-toast />
  </div>
</template>

<style scoped lang="scss">
.i-modal {
  .files-list {
    max-height: 200px;
    overflow-y: auto;

    .file-item {
      background: $light-neutral-20;
    }
  }

  .tags-list {
    flex-wrap: wrap;
    max-height: 100px;
    overflow-y: auto;
    overflow-x: hidden;
  }
}
</style>
